import { z } from 'zod';

export const CHANNEL_TYPES = ['email', 'telegram', 'webhook', 'bitrix24'] as const;
export type ChannelType = (typeof CHANNEL_TYPES)[number];

/**
 * The channel types staff can turn on for a workspace (spec §19 #67): the ones the platform delivers
 * to. A workspace creates channels only of the types it has on; channels it already has keep working
 * when their type is turned off.
 */
export const SWITCHABLE_CHANNEL_TYPES = ['email', 'webhook', 'bitrix24'] as const satisfies readonly ChannelType[];
export type SwitchableChannelType = (typeof SWITCHABLE_CHANNEL_TYPES)[number];
/** What a new workspace starts with. A type added later starts off everywhere, until staff turn it on. */
export const DEFAULT_CHANNEL_TYPES = ['email', 'webhook'] as const satisfies readonly SwitchableChannelType[];
export const channelTypesSchema = z
  .array(z.enum(SWITCHABLE_CHANNEL_TYPES))
  .max(SWITCHABLE_CHANNEL_TYPES.length)
  .refine((types) => new Set(types).size === types.length, 'must not name a type twice');

/** Text that ends up in a mail header: control characters (a line break above all) could start another header. */
const headerText = (max: number) =>
  z
    .string()
    .min(1)
    .max(max)
    // eslint-disable-next-line no-control-regex -- matching control characters is the point
    .regex(/^[^\u0000-\u001f\u007f]*$/u, 'must not contain control characters or line breaks');

/** FR-8.5 — an email destination. Templates use `{{placeholders}}` (packages/templates). */
export const emailChannelConfigSchema = z.object({
  recipients: z.array(z.email()).min(1).max(20),
  /** Subject template. Defaults to a generic one when omitted. */
  subject: headerText(200).optional(),
  /** Display name of the sender. The address itself is the platform's sending identity. */
  fromName: headerText(100).optional(),
});
export type EmailChannelConfig = z.infer<typeof emailChannelConfigSchema>;

// --- webhooks (FR-8.7) ------------------------------------------------------------------------------

/**
 * Headers the platform sets itself, or that HTTP reserves: a tenant's custom header may not be one
 * of them. `x-formplat-*` is the platform's own namespace.
 */
const RESERVED_WEBHOOK_HEADERS = ['host', 'content-type', 'content-length', 'content-encoding', 'transfer-encoding', 'connection', 'keep-alive', 'proxy-connection', 'upgrade', 'te', 'trailer', 'expect', 'user-agent', 'proxy-authorization', 'cookie'];

export const webhookHeaderNameSchema = z
  .string()
  .regex(/^[A-Za-z0-9][A-Za-z0-9-]{0,63}$/u, 'must be a header name: letters, digits and dashes')
  .refine((name) => !RESERVED_WEBHOOK_HEADERS.includes(name.toLowerCase()) && !name.toLowerCase().startsWith('x-formplat-'), 'is set by the platform and cannot be changed');

/**
 * A custom header's value: visible ASCII, spaces and tabs (RFC 9110 field values, and all that Node
 * sends). Anything else — a line break, which would start another header, or a non-Latin letter,
 * which Node refuses to send — would be saved here only to fail every delivery later.
 */
export const webhookHeaderValueSchema = z
  .string()
  .min(1)
  .max(1000)
  .regex(/^[\t\x20-\x7e]+$/u, 'must be plain ASCII text on one line');

/**
 * Where a webhook goes. Whether the host is a public address — and whether plain `http` is allowed —
 * is decided when it is saved and again at every send, by the outbound guard (P9, NFR-5).
 */
export const webhookUrlSchema = z
  .url({ protocol: /^https?$/u })
  .max(2000)
  // The address is shown to every member; a password in it would get round the sealed headers.
  .refine((value) => URL.canParse(value) && new URL(value).username === '' && new URL(value).password === '', 'must not contain a user name or password — send credentials in a header');

/** FR-8.7 — the public half of a webhook destination, stored in `channels.config` and shown in the dashboard. */
export const webhookChannelConfigSchema = z.object({
  url: webhookUrlSchema,
  /** The names of the custom headers sent with every call. Their values are secrets: see {@link webhookChannelSecretsSchema}. */
  headers: z.array(webhookHeaderNameSchema).max(10),
  /**
   * For the dashboard only: until when a rotated-out signing secret still signs calls. The worker
   * takes the time from the secret half, which is the one that counts.
   */
  previousSecretUntil: z.iso.datetime().nullable().default(null),
});
export type WebhookChannelConfig = z.infer<typeof webhookChannelConfigSchema>;

/**
 * The secret half, stored in `channels.config_encrypted` as JSON. Each value is sealed on its own to
 * the worker key pair (spec §18 #19), so a writer can replace one without reading the others — it
 * never can read any. `signing.previous` keeps a rotated-out secret valid until `until`, so that a
 * receiver has time to switch; while it lives, calls carry a signature by each (FR-8.7).
 */
export const webhookChannelSecretsSchema = z.object({
  signing: z.object({
    current: z.string().min(1),
    previous: z.object({ secret: z.string().min(1), until: z.iso.datetime() }).nullable(),
  }),
  /** Header name → its sealed value. The names are the same as in the public half. */
  headers: z.record(z.string(), z.string().min(1)),
});
export type WebhookChannelSecrets = z.infer<typeof webhookChannelSecretsSchema>;

/** How long a rotated-out signing secret keeps working. */
export const WEBHOOK_SECRET_OVERLAP_HOURS = 24;

// --- Bitrix24 (spec §19 #66) ------------------------------------------------------------------------

/** An inbound webhook of a Bitrix24 portal, taken apart. */
export interface Bitrix24Webhook {
  /** The portal's host (and port, if not the default): shown in the dashboard. */
  portal: string;
  /** The Bitrix24 user the webhook acts as: its leads are created in that user's name. */
  userId: number;
  /** `<scheme>://<portal>/rest/<user id>/<code>/` — the code is the credential, so this whole URL is a secret. */
  url: string;
}

const BITRIX24_WEBHOOK_PATH = /^\/rest\/([1-9][0-9]{0,9})\/([A-Za-z0-9]{8,64})(?:\/(?:[a-z][a-z0-9_.]{0,99})?)?\/?$/u;

/**
 * Reads the URL Bitrix24 shows for an inbound webhook ("Webhook to call REST API"), with or without a
 * method name after it (`…/crm.lead.add.json`), which is dropped — the platform chooses the methods.
 * A query or a fragment is dropped too. Returns `undefined` for anything else.
 */
export function parseBitrix24Webhook(value: string): Bitrix24Webhook | undefined {
  const trimmed = value.trim();
  if (!URL.canParse(trimmed)) return undefined;
  const url = new URL(trimmed);
  if ((url.protocol !== 'https:' && url.protocol !== 'http:') || url.username !== '' || url.password !== '') return undefined;
  const match = BITRIX24_WEBHOOK_PATH.exec(url.pathname);
  const [, userId, code] = match ?? [];
  // A user id is a 32-bit integer in Bitrix24 — and in the channel's configuration.
  if (userId === undefined || code === undefined || Number(userId) > 2_147_483_647) return undefined;
  return { portal: url.host, userId: Number(userId), url: `${url.protocol}//${url.host}/rest/${userId}/${code}/` };
}

/** The inbound webhook's URL as typed. Whether the host is public — and plain `http` allowed — is the outbound guard's call. */
export const bitrix24WebhookUrlSchema = z
  .string()
  .trim()
  .max(500)
  .refine((value) => parseBitrix24Webhook(value) !== undefined, 'must be the URL of a Bitrix24 inbound webhook: https://<portal>/rest/<user id>/<code>/');

/**
 * What a Bitrix24 channel creates (§19 #66, #75): a lead, or a deal with a contact — what a portal whose
 * CRM works without leads (its simple mode) can take. Chosen when the channel is created.
 */
export const BITRIX24_ENTITIES = ['lead', 'deal'] as const;
export type Bitrix24Entity = (typeof BITRIX24_ENTITIES)[number];

/** A lead source from the portal's list ("Source" in CRM settings). `WEB` is Bitrix24's own "Website". */
export const bitrix24SourceIdSchema = z.string().regex(/^[A-Za-z0-9_]{1,50}$/u, 'must be a source code from Bitrix24: letters, digits and underscores');
/** A Bitrix24 user id — who is responsible for the new leads. */
export const bitrix24UserIdSchema = z.number().int().positive().max(2_147_483_647);
/** A deal pipeline's id in Bitrix24 (`categoryId`, §19 #75). The pipeline a portal starts with is 0. */
export const bitrix24CategoryIdSchema = z.number().int().min(0).max(2_147_483_647);

// --- Bitrix24 field mapping (spec §19 #68) ---

/**
 * The kinds of lead field a form value can be written to. Others — money, files, users, links to other
 * CRM items, addresses — need more than one submitted value can say, and are not offered.
 */
export const BITRIX24_MAPPABLE_TYPES = ['string', 'integer', 'double', 'boolean', 'date', 'datetime', 'enumeration', 'url'] as const;
export type Bitrix24MappableType = (typeof BITRIX24_MAPPABLE_TYPES)[number];

/**
 * Lead and deal fields the platform fills itself (§19 #66, #75), and the item's own bookkeeping: a
 * mapping cannot point at them. Name, email and phone come from the field roles; title and comments
 * from the template; a deal's pipeline from the channel, and its contact is the one made with it.
 */
export const BITRIX24_RESERVED_TARGETS: readonly string[] = [
  'id',
  'title',
  'name',
  'comments',
  'fm',
  'phone',
  'email',
  'sourceId',
  'sourceDescription',
  'assignedById',
  'originatorId',
  'originId',
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'utmContent',
  'utmTerm',
  'categoryId',
  'contactId',
  'contactIds',
  'createdBy',
  'updatedBy',
  'movedBy',
  'createdTime',
  'updatedTime',
  'movedTime',
];

/** A lead field's name as `crm.item.fields` gives it: `lastName`, `companyTitle`, `ufCrm_1712345678901`. */
export const bitrix24FieldCodeSchema = z.string().regex(/^[A-Za-z][A-Za-z0-9_]{0,99}$/u, 'must be a field name from Bitrix24');
/** A list item's id in Bitrix24: digits. */
const bitrix24ItemIdSchema = z.string().regex(/^[0-9]{1,18}$/u, 'must be a list item id from Bitrix24');

/** One form field written to one lead field. `type` and `multiple` are the lead field's, copied when the mapping was saved. */
export const bitrix24FieldMappingSchema = z.object({
  /** The form field's key. A form without such a field simply has nothing to send there. */
  field: z.string().regex(/^[A-Za-z][A-Za-z0-9_-]{0,63}$/u, 'must be a form field key'),
  target: bitrix24FieldCodeSchema.refine((target) => !BITRIX24_RESERVED_TARGETS.includes(target), 'is filled by the platform itself'),
  type: z.enum(BITRIX24_MAPPABLE_TYPES),
  multiple: z.boolean(),
  /** For a list field: a submitted value (an option of the form field) → the id of the list item it stands for. */
  values: z.record(z.string().min(1).max(200), bitrix24ItemIdSchema).optional(),
});
export type Bitrix24FieldMapping = z.infer<typeof bitrix24FieldMappingSchema>;

export const bitrix24FieldMapSchema = z
  .array(bitrix24FieldMappingSchema)
  .max(100)
  .refine((mappings) => new Set(mappings.map((mapping) => mapping.field)).size === mappings.length, 'maps a form field twice: a form field goes to one lead field');

/** One lead or deal field of the portal, as the mapping editor offers it. Read from `crm.item.fields`. */
export const bitrix24CatalogFieldSchema = z.object({
  code: bitrix24FieldCodeSchema,
  title: z.string().max(255),
  /** Bitrix24's type name; only {@link BITRIX24_MAPPABLE_TYPES} can be mapped. */
  type: z.string().max(50),
  multiple: z.boolean(),
  required: z.boolean(),
  readOnly: z.boolean(),
  /** A field the portal's admins added (`ufCrm…`), not one of Bitrix24's own. */
  custom: z.boolean(),
  /** A list field's items. */
  items: z.array(z.object({ id: bitrix24ItemIdSchema, value: z.string().max(255) })).max(500).optional(),
});
export type Bitrix24CatalogField = z.infer<typeof bitrix24CatalogFieldSchema>;

/** One deal pipeline of the portal (§19 #75), as `crm.category.list` gives it. */
export const bitrix24CatalogCategorySchema = z.object({
  id: bitrix24CategoryIdSchema,
  name: z.string().max(255),
  /** The pipeline a deal goes to when none is chosen. */
  isDefault: z.boolean(),
});
export type Bitrix24CatalogCategory = z.infer<typeof bitrix24CatalogCategorySchema>;

/**
 * What the platform knows about the portal's fields — of leads or of deals, as the channel creates —
 * kept in `channels.catalog`. Only the worker can read the webhook's URL, so only the worker reads the
 * fields: when the channel is saved with a URL, and when someone asks for a refresh.
 */
export const bitrix24CatalogSchema = z.object({
  fields: z.array(bitrix24CatalogFieldSchema).max(1000),
  /** A deal channel's: the portal's deal pipelines the webhook's user can see (one page of `crm.category.list`). */
  categories: z.array(bitrix24CatalogCategorySchema).max(50).optional(),
  /** When `fields` were read; `null` — never yet. */
  fetchedAt: z.iso.datetime().nullable(),
  /** A refresh that is asked for and not finished: the dashboard waits while this is set. */
  requestedAt: z.iso.datetime().nullable(),
  /** The last refresh that failed, and why. A refresh that succeeds clears it. */
  error: z.object({ at: z.iso.datetime(), message: z.string().max(500) }).nullable(),
});
export type Bitrix24Catalog = z.infer<typeof bitrix24CatalogSchema>;

/** The public half of a Bitrix24 channel, stored in `channels.config` and shown in the dashboard. */
export const bitrix24ChannelConfigSchema = z.object({
  portal: z.string().min(1).max(260),
  /** The user the webhook belongs to. Not a secret: the code is. */
  userId: bitrix24UserIdSchema,
  /** Fixed when the channel is created: the portal's fields, the mapping and the checks made of the webhook all depend on it. */
  entity: z.enum(BITRIX24_ENTITIES).default('lead'),
  /** Who the lead or deal (and a deal's contact) is assigned to; `null` — Bitrix24 decides (the webhook's user, or the portal's rules). */
  assignedById: bitrix24UserIdSchema.nullable().default(null),
  sourceId: bitrix24SourceIdSchema.default('WEB'),
  /** A deal channel's pipeline (§19 #75); `null` — the portal's default pipeline. Always `null` for leads, which have none. */
  categoryId: bitrix24CategoryIdSchema.nullable().default(null),
  /** Form fields written to lead or deal fields of the portal's own (§19 #68), besides what the platform fills itself. */
  fieldMap: bitrix24FieldMapSchema.default([]),
});
export type Bitrix24ChannelConfig = z.infer<typeof bitrix24ChannelConfigSchema>;

/** The secret half, in `channels.config_encrypted` as JSON: the webhook's whole URL, sealed to the worker key pair. */
export const bitrix24ChannelSecretsSchema = z.object({ webhookUrl: z.string().min(1) });
export type Bitrix24ChannelSecrets = z.infer<typeof bitrix24ChannelSecretsSchema>;

/**
 * Written to a lead's — or a deal's and its contact's — `originatorId`, with the delivery id in
 * `originId`: how a retry finds what an earlier attempt created before its answer was lost, instead of
 * creating it a second time.
 */
export const BITRIX24_ORIGINATOR_ID = 'formplat';

/** The configuration of any channel type the platform delivers to today. */
export type ChannelConfig = EmailChannelConfig | WebhookChannelConfig | Bitrix24ChannelConfig;

/** A message template attached to a binding (FR-8.4). Absent parts fall back to the defaults. */
export const bindingTemplateSchema = z.object({
  subject: headerText(200).optional(),
  text: z.string().max(20_000).optional(),
});
export type BindingTemplate = z.infer<typeof bindingTemplateSchema>;
